import { z } from 'zod';
/**
 * Catálogo de permissões — fonte única para API, admin e seed.
 *
 * Formato do código: `<recurso>:<acao>` (ex.: `pessoas:criar`).
 * O RBAC é por PERFIL e por AÇÃO desde o dia 1; nenhuma rota mutante existe sem
 * uma permissão declarada.
 */
export const PERMISSION_ACTIONS = [
    'ver',
    'criar',
    'editar',
    'excluir',
    'aprovar',
    'exportar',
    /// Emitir é diferente de editar: é o ato que transforma o pedido em
    /// compromisso com o fornecedor. Quem digita não precisa ser quem assume.
    'emitir',
    'concluir',
    'excesso',
    'estornar',
    /// Amarrar a nota ao pedido/recebimento certo. Quem digita a nota não é
    /// quem decide de qual compra ela é.
    'vincular',
    /// Dar a nota por conferida (nota × pedido × recebimento) e tratar as
    /// divergências que a conferência abriu.
    'conferir',
    /// Liberar a nota para o Financeiro e o Estoque. É o ato que vira conta a
    /// pagar de verdade — separado de `editar` de propósito.
    'lancar',
    /// Comprar FORA do fluxo requisição → cotação → aprovação. Separado de
    /// `criar` de propósito: se fosse a mesma permissão, todo comprador teria
    /// a porta dos fundos aberta por padrão.
    'avulso',
];
export const permissionActionSchema = z.enum(PERMISSION_ACTIONS);
export const ACTION_LABELS = {
    ver: 'Ver',
    criar: 'Criar',
    editar: 'Editar',
    excluir: 'Excluir',
    aprovar: 'Aprovar',
    exportar: 'Exportar',
    emitir: 'Emitir',
    concluir: 'Concluir',
    excesso: 'Aceitar excesso',
    estornar: 'Estornar',
    vincular: 'Vincular pedido/recebimento',
    conferir: 'Conferir e tratar divergência',
    lancar: 'Lançar no Financeiro/Estoque',
    avulso: 'Criar avulso',
};
/** Agrupador de recursos na tela de perfis — espelha a sidebar. */
export const PERMISSION_MODULES = [
    'cadastros',
    'financeiro',
    'compras',
    'estoque',
    'associados',
    'rh',
    'relatorios',
    'agentes',
    'configuracoes',
];
export const MODULE_LABELS = {
    cadastros: 'Cadastros',
    financeiro: 'Financeiro',
    compras: 'Compras',
    estoque: 'Estoque',
    associados: 'Associados',
    rh: 'RH',
    relatorios: 'Relatórios',
    agentes: 'Agentes',
    configuracoes: 'Configurações',
};
/**
 * Recursos existentes hoje. Módulos futuros (Day Use, PDV, Acesso...) entram
 * aqui quando forem construídos — a estrutura já comporta.
 */
export const PERMISSION_RESOURCES = [
    {
        key: 'pessoas',
        label: 'Pessoas',
        module: 'cadastros',
        actions: ['ver', 'criar', 'editar', 'excluir', 'exportar'],
    },
    {
        key: 'categorias',
        label: 'Categorias',
        module: 'cadastros',
        actions: ['ver', 'criar', 'editar', 'excluir'],
    },
    {
        key: 'centros-custo',
        label: 'Centros de custo',
        module: 'financeiro',
        actions: ['ver', 'criar', 'editar', 'excluir'],
    },
    {
        key: 'plano-contas',
        label: 'Plano de contas',
        module: 'financeiro',
        actions: ['ver', 'criar', 'editar', 'excluir'],
    },
    {
        key: 'lancamentos',
        label: 'Lançamentos financeiros',
        module: 'financeiro',
        actions: ['ver', 'criar', 'editar', 'excluir', 'aprovar', 'exportar'],
    },
    {
        // Empresa do GRUPO para o financeiro (AMAI PARK, BELLA CENTER). Não é a
        // `empresas` de Configurações, que é a instalação: esta é o recorte da
        // DRE e diz de qual banco a conta pode ser baixada.
        key: 'empresas-financeiro',
        label: 'Empresas do grupo (financeiro)',
        module: 'financeiro',
        actions: ['ver', 'criar', 'editar'],
    },
    {
        key: 'contas-bancarias',
        label: 'Contas bancárias e caixas',
        module: 'financeiro',
        actions: ['ver', 'criar', 'editar', 'excluir'],
    },
    {
        key: 'cobrancas',
        label: 'Cobranças (PIX, boleto e cartão)',
        module: 'financeiro',
        // `estornar` é separado de `excluir` porque cobrança não se exclui: o que
        // existe é devolver o dinheiro, e isso é um contra-lançamento.
        actions: ['ver', 'criar', 'excluir', 'estornar', 'exportar'],
    },
    {
        key: 'gateway-pagamento',
        label: 'Ligação com o gateway de pagamento',
        module: 'configuracoes',
        // Quem tem a chave da API cobra em nome da empresa e mexe no dinheiro que
        // entra. Recurso próprio, fora de `contas-bancarias`, para poder ser dado
        // só ao Diretor.
        actions: ['ver', 'editar'],
    },
    {
        key: 'requisicoes',
        label: 'Requisições de compra',
        module: 'compras',
        actions: ['ver', 'criar', 'editar', 'excluir', 'aprovar'],
    },
    {
        key: 'cotacoes',
        label: 'Cotações',
        module: 'compras',
        actions: ['ver', 'criar', 'editar', 'excluir', 'aprovar'],
    },
    {
        key: 'pedidos-compra',
        label: 'Pedidos de compra',
        module: 'compras',
        actions: ['ver', 'criar', 'editar', 'excluir', 'aprovar', 'emitir', 'avulso'],
    },
    {
        key: 'recebimentos',
        label: 'Recebimentos',
        module: 'compras',
        // `concluir` dá entrada no estoque; `excesso` aceita mais do que o
        // pedido; `estornar` desfaz o que já entrou. Cada um é uma porta que
        // não se abre junto com "criar".
        actions: ['ver', 'criar', 'editar', 'excluir', 'concluir', 'excesso', 'estornar'],
    },
    {
        key: 'documentos-fiscais',
        label: 'Documentos e NF-e',
        module: 'compras',
        // `lancar` é o que transforma a nota em conta a pagar; `vincular` decide
        // de qual compra ela é; `conferir` fecha a divergência. Nenhum desses
        // cabe dentro de "editar".
        actions: ['ver', 'criar', 'editar', 'excluir', 'vincular', 'conferir', 'lancar', 'exportar'],
    },
    {
        key: 'itens',
        label: 'Itens e materiais',
        module: 'estoque',
        actions: ['ver', 'criar', 'editar', 'excluir', 'exportar'],
    },
    {
        key: 'locais-estoque',
        label: 'Locais de estoque',
        module: 'estoque',
        actions: ['ver', 'criar', 'editar', 'excluir'],
    },
    {
        key: 'movimentacoes',
        label: 'Movimentações de estoque',
        module: 'estoque',
        actions: ['ver', 'criar', 'aprovar', 'exportar'],
    },
    {
        key: 'inventarios',
        label: 'Inventários',
        module: 'estoque',
        actions: ['ver', 'criar', 'editar', 'excluir', 'aprovar'],
    },
    {
        key: 'planos-socios',
        label: 'Planos do clube',
        module: 'associados',
        actions: ['ver', 'criar', 'editar', 'excluir'],
    },
    {
        key: 'associados',
        label: 'Associados',
        module: 'associados',
        actions: ['ver', 'criar', 'editar', 'excluir', 'aprovar', 'exportar'],
    },
    {
        key: 'mensalidades',
        label: 'Mensalidades do clube',
        module: 'associados',
        actions: ['ver', 'criar'],
    },
    {
        key: 'inadimplencia',
        label: 'Régua de inadimplência',
        module: 'associados',
        /// `editar` mexe na régua (dias e ações); `criar` é MANDAR a régua rodar
        /// agora, que avisa associado e fecha catraca. São coisas diferentes.
        actions: ['ver', 'criar', 'editar'],
    },
    {
        key: 'restritos',
        label: 'Pessoas restritas',
        module: 'associados',
        actions: ['ver', 'criar', 'excluir'],
    },
    {
        key: 'colaboradores',
        label: 'Colaboradores',
        module: 'rh',
        actions: ['ver', 'criar', 'editar', 'excluir', 'exportar'],
    },
    {
        key: 'departamentos',
        label: 'Departamentos e cargos',
        module: 'rh',
        actions: ['ver', 'criar', 'editar', 'excluir'],
    },
    {
        key: 'afastamentos',
        label: 'Férias e afastamentos',
        module: 'rh',
        actions: ['ver', 'criar', 'editar', 'excluir', 'aprovar'],
    },
    {
        key: 'folha-pagamento',
        label: 'Folha de pagamento',
        module: 'rh',
        actions: ['ver', 'criar', 'editar', 'excluir', 'aprovar', 'exportar'],
    },
    {
        key: 'empresas',
        label: 'Empresas e unidades',
        module: 'configuracoes',
        actions: ['ver', 'criar', 'editar', 'excluir'],
    },
    {
        key: 'usuarios',
        label: 'Usuários',
        module: 'configuracoes',
        actions: ['ver', 'criar', 'editar', 'excluir'],
    },
    {
        key: 'perfis',
        label: 'Perfis e permissões',
        module: 'configuracoes',
        actions: ['ver', 'criar', 'editar', 'excluir'],
    },
    {
        key: 'alcadas',
        label: 'Faixas de alçada de compra',
        module: 'configuracoes',
        actions: ['ver', 'criar', 'editar', 'excluir'],
    },
    {
        key: 'api-keys',
        label: 'Chaves de API',
        module: 'configuracoes',
        actions: ['ver', 'criar', 'editar', 'excluir'],
    },
    {
        key: 'certificado-digital',
        label: 'Certificado digital (SEFAZ)',
        module: 'configuracoes',
        actions: ['ver', 'criar', 'excluir'],
    },
    {
        key: 'auditoria',
        label: 'Trilha de auditoria',
        module: 'configuracoes',
        actions: ['ver', 'exportar'],
    },
    {
        key: 'canal-mensagens',
        label: 'Canal de mensagens (WhatsApp)',
        module: 'agentes',
        actions: ['ver', 'editar', 'excluir'],
    },
    {
        /// `criar` aqui é MANDAR mensagem — quem tem só `ver` lê o histórico e não
        /// dispara nada. Separado do canal de propósito: configurar o WhatsApp da
        /// empresa e falar em nome dela são poderes diferentes.
        key: 'mensagens',
        label: 'Mensagens',
        module: 'agentes',
        actions: ['ver', 'criar'],
    },
];
export const permissionCodeSchema = z
    .string()
    .regex(/^[a-z][a-z0-9-]*:[a-z]+$/, 'Código de permissão inválido');
export function permissionCode(resource, action) {
    return `${resource}:${action}`;
}
/** Todas as permissões existentes, achatadas. Usado no seed e na tela de perfis. */
export const ALL_PERMISSIONS = PERMISSION_RESOURCES.flatMap((resource) => resource.actions.map((action) => ({
    code: permissionCode(resource.key, action),
    resource: resource.key,
    action,
    module: resource.module,
    label: `${ACTION_LABELS[action]} · ${resource.label}`,
})));
export const ALL_PERMISSION_CODES = ALL_PERMISSIONS.map((p) => p.code);
const viewAll = (resources) => resources.map((resource) => permissionCode(resource, 'ver'));
const manage = (resources) => resources.flatMap((resource) => {
    const found = PERMISSION_RESOURCES.find((r) => r.key === resource);
    return (found?.actions ?? []).map((action) => permissionCode(resource, action));
});
/**
 * Como `manage`, mas sem as ações listadas.
 *
 * Serve à alçada: quem opera o almoxarifado registra entrada, saída e
 * transferência, mas NÃO aprova o próprio ajuste de saldo nem fecha o próprio
 * inventário — senão a alçada seria só decoração.
 */
const manageExcept = (resources, except) => resources.flatMap((resource) => {
    const found = PERMISSION_RESOURCES.find((r) => r.key === resource);
    return (found?.actions ?? [])
        .filter((action) => !except.includes(action))
        .map((action) => permissionCode(resource, action));
});
export const DEFAULT_PROFILES = [
    {
        slug: 'diretor',
        name: 'Diretor',
        description: 'Acesso total, incluindo aprovações e configurações da plataforma.',
        fullAccess: true,
        permissions: ALL_PERMISSION_CODES,
    },
    {
        slug: 'financeiro',
        name: 'Financeiro',
        description: 'Lançamentos, centros de custo, plano de contas e relatórios financeiros.',
        fullAccess: false,
        permissions: [
            ...manage([
                'centros-custo',
                'plano-contas',
                'lancamentos',
                'contas-bancarias',
                'empresas-financeiro',
            ]),
            ...manage(['pessoas', 'folha-pagamento']),
            // Emitir e estornar cobrança é trabalho do Financeiro. A CHAVE do
            // gateway não: quem a tem cobra em nome da empresa, e isso fica com o
            // Diretor.
            ...manage(['cobrancas']),
            // A receita do clube nasce aqui: Financeiro enxerga tudo e é quem
            // ativa o associado (aprovar = contrato aceito + pagamento em dia).
            ...viewAll(['planos-socios', 'associados']),
            permissionCode('associados', 'aprovar'),
            permissionCode('associados', 'exportar'),
            // A manutenção do associado é conta a receber: quem gera é o Financeiro.
            ...manage(['mensalidades', 'inadimplencia']),
            ...viewAll([
                'categorias',
                'auditoria',
                'pedidos-compra',
                'recebimentos',
                'colaboradores',
            ]),
            // A nota conferida pelo almoxarifado vira conta a pagar aqui.
            permissionCode('documentos-fiscais', 'ver'),
            permissionCode('documentos-fiscais', 'lancar'),
            permissionCode('documentos-fiscais', 'exportar'),
            // O resumo de contas a pagar é mensagem do Financeiro. Mandar, sim;
            // configurar o WhatsApp da empresa, não.
            permissionCode('mensagens', 'ver'),
            permissionCode('mensagens', 'criar'),
        ],
    },
    {
        slug: 'compras',
        name: 'Compras / Almoxarifado',
        description: 'Fornecedores, requisições, cotações, pedidos, recebimento e estoque.',
        fullAccess: false,
        permissions: [
            ...manage(['pessoas', 'categorias']),
            ...manage(['itens', 'locais-estoque']),
            // Compras importa a nota, vincula ao pedido e confere. LANÇAR no
            // Financeiro é do Financeiro — é o passo que vira dinheiro a pagar.
            ...manageExcept(['documentos-fiscais'], ['lancar']),
            // O almoxarifado confere e conclui; aceitar EXCESSO e ESTORNAR o que
            // já entrou no estoque é decisão do Diretor.
            ...manageExcept(['recebimentos'], ['excesso', 'estornar']),
            // Compras monta a requisição, a cotação e o pedido; quem APROVA é o
            // Diretor. E o ajuste de saldo e o fechamento de inventário também
            // ficam fora — é o mesmo princípio de quem pede não ser quem autoriza.
            // Compras monta, emite e registra a compra de emergência; quem APROVA é
            // o Diretor. Tirar `avulso` daqui parecia mais seguro e era pior: só o
            // Diretor podendo criar E aprovar, nenhum pedido avulso passaria — a
            // separação some justamente onde ela importa.
            ...manageExcept(['requisicoes', 'cotacoes', 'pedidos-compra', 'movimentacoes', 'inventarios'], ['aprovar']),
            ...viewAll(['centros-custo', 'plano-contas', 'lancamentos', 'empresas-financeiro']),
        ],
    },
    {
        slug: 'operacional',
        name: 'Operacional',
        description: 'Rotina do dia a dia, sem acesso a configurações da plataforma.',
        fullAccess: false,
        permissions: [
            permissionCode('pessoas', 'ver'),
            permissionCode('pessoas', 'criar'),
            permissionCode('pessoas', 'editar'),
            permissionCode('requisicoes', 'ver'),
            permissionCode('requisicoes', 'criar'),
            permissionCode('requisicoes', 'editar'),
            permissionCode('movimentacoes', 'ver'),
            permissionCode('movimentacoes', 'criar'),
            // Balcão do clube: vende (cria) e corrige cadastro; ativar é do
            // Financeiro/Diretor. Restritos ele consulta na portaria.
            permissionCode('planos-socios', 'ver'),
            permissionCode('associados', 'ver'),
            permissionCode('associados', 'criar'),
            permissionCode('associados', 'editar'),
            permissionCode('restritos', 'ver'),
            ...viewAll([
                'categorias',
                'centros-custo',
                'plano-contas',
                'itens',
                'colaboradores',
            ]),
        ],
    },
    {
        slug: 'leitura',
        name: 'Leitura',
        description: 'Somente consulta. Não cria, não edita, não aprova.',
        fullAccess: false,
        permissions: viewAll([
            'planos-socios',
            'associados',
            'mensalidades',
            'inadimplencia',
            'restritos',
            'pessoas',
            'categorias',
            'centros-custo',
            'plano-contas',
            'lancamentos',
            'contas-bancarias',
            'empresas-financeiro',
            'requisicoes',
            'cotacoes',
            'pedidos-compra',
            'recebimentos',
            'documentos-fiscais',
            'itens',
            'locais-estoque',
            'movimentacoes',
            'inventarios',
            'colaboradores',
            'departamentos',
            'afastamentos',
            'folha-pagamento',
            'empresas',
            'usuarios',
            'perfis',
            'certificado-digital',
            'auditoria',
            // Só o HISTÓRICO. A configuração do canal expõe o WABA e o número da
            // empresa, e o perfil de leitura não tem o que fazer com isso.
            'mensagens',
        ]),
    },
];
/**
 * Decide se um conjunto de permissões autoriza uma ação.
 * `fullAccess` (Diretor) passa sempre — inclusive por recursos futuros.
 */
export function can(granted, code) {
    if (granted.fullAccess)
        return true;
    return granted.permissions.includes(code);
}
//# sourceMappingURL=permissions.js.map